import React, { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { isEmpty, findLast, uniqBy } from "lodash";
import { useQuery, useMutation } from "@apollo/client";

import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import Button from "@material-ui/core/Button";
import Link from "@material-ui/core/Link";
import MuiTextField from "@material-ui/core/TextField";

import { text2html } from "../../shared/utils/text2html";

import {
  GET_DOCTOR_PATIENT_MESSAGES,
  CREATE_PATIENT_DOCTOR_MESSAGE,
  CREATE_PATIENT_READ_RECEIPT_ON_LATEST_MESSAGE_FROM_DOCTOR,
} from "./portalQueries";

import { usePortalStyles, kickColors } from "../styles/portalStyles";

let cacheLatestCareMessageID = "";

function GetDoctorPatientMessages() {
  const [
    createPatientReadReceiptOnLatestMessageFromDoctor,
    { newReadReceipt },
  ] = useMutation(CREATE_PATIENT_READ_RECEIPT_ON_LATEST_MESSAGE_FROM_DOCTOR);

  const checkAndCreateReceiptOnLatest = async (data) => {
    // grab the last message from an EMR User
    let lastMessageFromEMRUser = findLast(data.doctorPatientMessages, (m) => {
      return m.emrUserLastName != "";
    });

    // create a read receipt if the last message has changed
    if (
      lastMessageFromEMRUser &&
      lastMessageFromEMRUser.id != cacheLatestCareMessageID
    ) {
      let results = await createPatientReadReceiptOnLatestMessageFromDoctor();

      cacheLatestCareMessageID =
        results.data.createPatientReadReceiptOnLatestMessageFromDoctor
          .doctorPatientMessageID;
    }
  };

  const { loading, error, data } = useQuery(GET_DOCTOR_PATIENT_MESSAGES, {
    pollInterval: 30 * 1000,
  });

  if (loading) return <p>Loading...</p>;
  if (error) return <p>{`Error! ${error.message}`}</p>;

  checkAndCreateReceiptOnLatest(data);

  return <PatientTabMessagesList messages={data} />;
}

function PatientTabMessagesList(data) {
  const classes = usePortalStyles();
  // console.log("!! Messages", data.messages);
  const noMessagesYet = isEmpty(data.messages.doctorPatientMessages);
  const messagesEndRef = useRef(null);
  const messagesListRef = useRef(null);

  useEffect(() => {
    // messagesEndRef.current &&
    //   messagesEndRef.current.scrollIntoView({ behavior: "smooth" });
    // messagesEndRef.current.scrollTop = messagesEndRef.current.scrollHeight;
    // scroller.scrollTo("messagesEnd", {
    //   // duration: 1500,
    //   isDynamic: true,
    //   delay: 1000,
    //   smooth: true,
    //   containerId: "messagesList",
    //   offset: 1000, // Scrolls to element + 50 pixels down the page
    // });

    // messagesListRef.current &&
    messagesListRef.current.scrollTop = messagesListRef.current.scrollHeight;
  });

  return (
    <React.Fragment>
      <div
        id="messagesList"
        ref={messagesListRef}
        style={{
          display: "flex",
          alignItems: noMessagesYet ? "center" : "flex-start",
          justifyContent: noMessagesYet ? "center" : "flex-start",
          flexDirection: "column",
          width: "100%",
          marginBottom: "20px",
          paddingBottom: "10px",
          // paddingLeft: "20px",
          // paddingRight: "20px",
          overflowY: "auto",
          flexGrow: 1,
          // backgroundColor: `radial-gradient(circle at 100% 100%, rgba(lightblue, 0.8),rgba(pink, 0.05)), radial-gradient(circle at 100% 0%, rgba(#e3ade6, 0.05), rgba(#e3ade6, 0.8)), radial-gradient(circle at 20% 50%, rgba(yellow, 0.5), rgba(pink, 0.1))`,
          // backgroundColor: "white",

          // border: "3px solid red",
        }}
      >
        <h3 style={{ width: "100%", textAlign: "center" }}>Text Your Doctor</h3>
        <Divider style={{ margin: "6px 0 4px 0" }} />

        <Typography
          style={{ width: "100%", marginBottom: "16px", textAlign: "center" }}
        >
          Send a secure, private text to your doctor. We'll send you an email
          notification when they respond.
          <br />
          If you have an order related question,{" "}
          <Link href="/me/messages/care">message us</Link> instead.
        </Typography>

        {data.messages.doctorPatientMessages.map((row) => {
          const fromPatient = isEmpty(row.emrUserLastName);
          const messageRawMarkup = {
            __html: text2html(row && row.message),
          };

          return (
            <div
              key={row.id}
              style={{
                display: "flex",
                flexShrink: 0,
                flexDirection: "row",
                marginTop: "10px",
                marginLeft: "20px",
                marginRight: "20px",
                padding: "18px 10px 18px 10px",
                fontSize: "16px",
                width: "70%",

                background: fromPatient
                  ? // ? "linear-gradient(#5867fb, #3F51B5)"
                    "linear-gradient(20deg, #3F51B5 25%, #3f3f82 80%)"
                  : `linear-gradient(20deg, #f7fcff 25%, 60%, #fff2f0 80%)`,

                color: fromPatient ? "white" : kickColors.navy,
                alignSelf: fromPatient ? "flex-end" : "flex-start",
                borderRadius: "10px",
              }}
            >
              <div style={{ flex: 1, flexDirection: "column" }}>
                <div style={{ margin: "0" }}>
                  <span dangerouslySetInnerHTML={messageRawMarkup}></span>

                  {!fromPatient ? (
                    <div style={{ marginTop: "12px", fontSize: "12px" }}>
                      {"From "}
                      <strong>
                        {row.emrUserIsDoctor && "Dr. "}
                        {`${row.emrUserFirstName} ${row.emrUserLastName}`}
                      </strong>
                      {" at "} {new Date(row.createdAt).toLocaleString()}
                    </div>
                  ) : (
                    <div style={{ marginTop: "12px", fontSize: "12px" }}>
                      {new Date(row.createdAt).toLocaleString()}
                    </div>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>
      <div name="messagesEnd" ref={messagesEndRef} />
    </React.Fragment>
  );
}

function renderMessageInput() {
  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { isSubmitting, errors },
  } = useForm();

  const [sendMessage, { data }] = useMutation(CREATE_PATIENT_DOCTOR_MESSAGE, {
    update(cache, { data: { createPatientDoctorMessage } }) {
      // update the cache
      const data = cache.readQuery({
        query: GET_DOCTOR_PATIENT_MESSAGES,
      });
      cache.writeQuery({
        query: GET_DOCTOR_PATIENT_MESSAGES,
        data: {
          doctorPatientMessages: uniqBy(
            [...data.doctorPatientMessages, createPatientDoctorMessage],
            "id"
          ),
        },
      });
    },
  });

  // for react-hook-form
  const { registerRef, ...registerRest } = register("message");

  return (
    <React.Fragment>
      <Divider style={{ marginTop: "6px" }} />
      <form
        style={{
          width: "98%",
          marginTop: "10px",
          display: "flex",
          // alignItems: "center",
          alignSelf: "center",
          flexDirection: "column",
        }}
        onSubmit={handleSubmit(async (data, e) => {
          let result = await sendMessage({
            variables: { message: data.message },
          });

          e.target.reset();
          reset();
        })}
      >
        <MuiTextField
          inputRef={registerRef}
          {...registerRest}
          name="message"
          placeholder="What would you like to tell your doc...?"
          multiline
          fullWidth
          inputProps={{ minRows: 3 }}
          variant="outlined"
          style={{
            backgroundColor: "white",
          }}
        />
        <div style={{ display: "flex", flexDirection: "row-reverse" }}>
          <Button
            type="submit"
            disabled={isSubmitting}
            style={{
              display: "block",
              backgroundColor: kickColors.blue,
              color: "white",
              width: "130px",
              marginTop: "10px",
              marginBottom: "10px",
              // marginRight: "10px",
            }}
          >
            Send Text
          </Button>
        </div>
      </form>
    </React.Fragment>
  );
}

export default function PortalDoctorMessages(props) {
  const classes = usePortalStyles();

  return (
    <main
      className={classes.content}
      style={{
        display: "flex",
        // flexWrap: "wrap",
        flexDirection: "column",
        alignContent: "space-between",
        alignItems: "center",
        // flexShrink: 0,
        // width: "95%",
        // border: "4px solid green",
      }}
    >
      <GetDoctorPatientMessages />

      <div
        style={{
          justifyContent: "flex-end",
          display: "flex",
          width: "100%",
          // border: "1px solid red",
          // padding: "4px 10px 10px 10px",
          marginTop: "auto", // pin to bottom of dscreen
          height: "160px",
          flexDirection: "column",
        }}
      >
        {renderMessageInput()}
      </div>
    </main>
  );
}
